import React from "react";
import { AiSparkleIcon } from "../../atoms/AiSparkleIcon";
import { FormField } from "../FormField";
import { StoryAudienceSection } from "../StoryAudienceSection";
import { audienceKeyPatch } from "../../../services/dataStories/storyDetailsAudience";
import { Fields } from "./BlankCanvasDetailsStep.styles";
import type { BlankCanvasDetailsStepProps } from "./BlankCanvasDetailsStep.types";

/** Figma draws the Goal label's "Logo" sparkle at 16. */
const GOAL_ICON_SIZE = 16;

/**
 * Blank-canvas details step body — Figma 5326:88287.
 *
 * Stateless: the whole form lives in `values` and every edit goes out as a
 * patch, so the same body can be driven by the flow organism, a Storybook
 * story, or a test with no change.
 */
export const BlankCanvasDetailsStep: React.FC<BlankCanvasDetailsStepProps> = ({
  values,
  onChange,
}) => {
  const handleAudienceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const patch = audienceKeyPatch(e.key, values);
    if (!patch) return;
    // The step body sits inside a modal; without this the Enter would bubble
    // to the form/dialog and trip the footer's Continue.
    e.preventDefault();
    onChange(patch);
  };

  return (
    <Fields>
      <FormField
        label="Story name"
        surface="plain"
        placeholder="Story name..."
        value={values.name}
        onChange={(e) => onChange({ name: e.target.value })}
      />

      <FormField
        label="Goal"
        labelIcon={<AiSparkleIcon size={GOAL_ICON_SIZE} />}
        surface="plain"
        placeholder="Add goal of your story"
        value={values.goal}
        onChange={(e) => onChange({ goal: e.target.value })}
      />

      <StoryAudienceSection
        variant="modal"
        audiences={values.audiences}
        audienceInput={values.audienceDraft}
        isAddingAudience={values.isAddingAudience}
        onAudienceInputChange={(audienceDraft) => onChange({ audienceDraft })}
        onStartAddingAudience={() => onChange({ isAddingAudience: true })}
        onAudienceKeyDown={handleAudienceKeyDown}
        onRemoveAudience={(audience) =>
          onChange({
            audiences: values.audiences.filter((a) => a !== audience),
          })
        }
      />
    </Fields>
  );
};

export default BlankCanvasDetailsStep;
